import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Crosshair,
  Phone,
  Mail,
  Save,
  Loader2,
  Check,
  DollarSign,
  Clock,
  MapPin,
  Wrench,
  Sparkles,
  Activity,
  Send,
  FileText,
  ChevronDown,
} from "lucide-react";
import SalesCritique from "@/components/SalesCritique";

const STATUS_OPTIONS = ["Target", "Contacted", "Audit Sent", "Closed", "Active Customer"];

const STATUS_STYLE = {
  "Target": { color: "#94A3B8", bg: "rgba(148,163,184,0.12)", border: "rgba(148,163,184,0.4)" },
  "Contacted": { color: "#60A5FA", bg: "rgba(96,165,250,0.14)", border: "rgba(96,165,250,0.5)" },
  "Audit Sent": { color: "#D4AF37", bg: "rgba(212,175,55,0.14)", border: "rgba(212,175,55,0.55)" },
  "Closed": { color: "#34D399", bg: "rgba(52,211,153,0.14)", border: "rgba(52,211,153,0.5)" },
  "Active Customer": { color: "#A78BFA", bg: "rgba(167,139,250,0.14)", border: "rgba(167,139,250,0.55)" },
};

const SERVICE_LEVELS = [
  "L1 Reputation",
  "L2 Lead Net",
  "L3 Content Engine",
  "L4 Voice",
  "L5 Accelerator",
];

const HEALTH_OPTIONS = [
  { key: "Active", label: "Active", dot: "#22c55e", ring: "rgba(34,197,94,0.35)" },
  { key: "Past Due", label: "Past Due", dot: "#eab308", ring: "rgba(234,179,8,0.35)" },
  { key: "Churned", label: "Churned", dot: "#ef4444", ring: "rgba(239,68,68,0.35)" },
];

function fillTemplate(template, lead, v4Url) {
  if (!template) return "";
  return template
    .replaceAll("{contact_name}", lead.contact_name || "there")
    .replaceAll("{business_name}", lead.business_name || "your business")
    .replaceAll("{city}", lead.city || "Ohio")
    .replaceAll("{industry}", lead.industry || "local")
    .replaceAll("{v4_url}", v4Url || "");
}

export default function LeadCockpit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [status, setStatus] = useState("Target");
  const [salesNotes, setSalesNotes] = useState("");
  const [weeklyLeak, setWeeklyLeak] = useState("");
  const [serviceLevel, setServiceLevel] = useState("");
  const [subscriptionHealth, setSubscriptionHealth] = useState("");
  const [saving, setSaving] = useState(false);
  const [initial, setInitial] = useState(null);

  // Composer state
  const [templates, setTemplates] = useState([]);
  const [v4Url, setV4Url] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("tmpl-manifesto");
  const [draftSubject, setDraftSubject] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [emailState, setEmailState] = useState("idle"); // idle | sending | sent | error

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/leader/leads/${id}`);
        const l = data.lead;
        setLead(l);
        setStatus(l.status || "Target");
        setSalesNotes(l.sales_notes || "");
        setWeeklyLeak(l.weekly_leak ? String(l.weekly_leak) : "");
        setServiceLevel(l.service_level || "");
        setSubscriptionHealth(l.subscription_health || "");
        setInitial({
          status: l.status || "Target",
          sales_notes: l.sales_notes || "",
          weekly_leak: l.weekly_leak || 0,
          service_level: l.service_level || "",
          subscription_health: l.subscription_health || "",
        });
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
        else toast.error("Failed to load lead");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Fetch email templates once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/leader/email-templates");
        setTemplates(data.templates || []);
        setV4Url(data.v4_url || "");
      } catch (e) {
        // Non-fatal — composer will fall back to blank
      }
    })();
  }, []);

  // When lead + templates are ready, initialize draft with the default template
  useEffect(() => {
    if (!lead || templates.length === 0) return;
    const t = templates.find((x) => x.id === selectedTemplateId) || templates[0];
    if (!t) return;
    setSelectedTemplateId(t.id);
    setDraftSubject(fillTemplate(t.subject, lead, v4Url));
    setDraftBody(fillTemplate(t.body, lead, v4Url));
  }, [lead, templates]);

  const applyTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    if (emailState === "sent") setEmailState("idle");
    const t = templates.find((x) => x.id === templateId);
    if (!t) {
      setDraftSubject("");
      setDraftBody("");
      return;
    }
    setDraftSubject(fillTemplate(t.subject, lead, v4Url));
    setDraftBody(fillTemplate(t.body, lead, v4Url));
  };

  const initials = useMemo(
    () =>
      lead?.business_name
        ?.split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase() || "?",
    [lead]
  );

  const isCustomer = status === "Active Customer";

  const isDirty = useMemo(() => {
    if (!initial) return false;
    return (
      status !== initial.status ||
      salesNotes !== initial.sales_notes ||
      Number(weeklyLeak || 0) !== Number(initial.weekly_leak || 0) ||
      serviceLevel !== initial.service_level ||
      subscriptionHealth !== initial.subscription_health
    );
  }, [status, salesNotes, weeklyLeak, serviceLevel, subscriptionHealth, initial]);

  const save = async () => {
    if (saving) return;
    const leakNum = weeklyLeak === "" ? 0 : Number(weeklyLeak);
    if (Number.isNaN(leakNum) || leakNum < 0) {
      toast.error("Weekly leak must be a positive number");
      return;
    }
    setSaving(true);
    try {
      // If switching away from Active Customer, clear the customer-only fields
      const payload = {
        status,
        sales_notes: salesNotes,
        weekly_leak: leakNum,
        service_level: isCustomer ? serviceLevel : "",
        subscription_health: isCustomer ? subscriptionHealth : "",
      };
      await api.patch(`/leader/leads/${id}`, payload);
      setInitial({
        status,
        sales_notes: salesNotes,
        weekly_leak: leakNum,
        service_level: payload.service_level,
        subscription_health: payload.subscription_health,
      });
      if (!isCustomer) {
        setServiceLevel("");
        setSubscriptionHealth("");
      }
      toast.success("Cockpit updated");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const slaySend = async () => {
    if (!lead || emailState === "sending" || emailState === "sent") return;
    const subject = draftSubject.trim();
    const body = draftBody.trim();
    if (!subject) {
      toast.error("Subject cannot be empty");
      return;
    }
    if (!body) {
      toast.error("Body cannot be empty");
      return;
    }
    setEmailState("sending");
    try {
      await api.post("/strike-list/send-email", {
        targetId: lead.id,
        toEmail: lead.email,
        subject,
        body,
      });
      setEmailState("sent");
      toast.success(`Email sent to ${lead.business_name}`);
    } catch (err) {
      const s = err.response?.status;
      if (s === 503) {
        const mailto = `mailto:${lead.email}?subject=${encodeURIComponent(
          subject
        )}&body=${encodeURIComponent(body)}`;
        window.location.href = mailto;
        setEmailState("idle");
        toast.info("Email service offline — opening your local mail client.");
        return;
      }
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Send failed");
      setEmailState("error");
      setTimeout(() => setEmailState("idle"), 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060B14]">
        <Header />
        <main className="max-w-6xl mx-auto px-6 lg:px-10 py-16 text-center text-slate-500">
          Loading cockpit...
        </main>
      </div>
    );
  }

  if (notFound || !lead) {
    return (
      <div className="min-h-screen bg-[#060B14]">
        <Header />
        <main className="max-w-6xl mx-auto px-6 lg:px-10 py-16 text-center" data-testid="cockpit-notfound">
          <div className="hud-label mb-3">Lead Not Found</div>
          <h1 className="font-display text-3xl text-white mb-6">This lead is off the register.</h1>
          <button
            onClick={() => navigate("/leader")}
            data-testid="cockpit-return-empty"
            className="btn-ghost-gold px-6 py-3 rounded-sm text-sm inline-flex items-center gap-2"
          >
            <ArrowLeft size={14} /> Back to Strike List
          </button>
        </main>
      </div>
    );
  }

  const statusCfg = STATUS_STYLE[status] || STATUS_STYLE["Target"];

  return (
    <div className="min-h-screen bg-[#060B14]" data-testid="lead-cockpit-page">
      <Header />

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/leader"
            state={{ tab: "strike" }}
            data-testid="cockpit-back-link"
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold transition-colors"
          >
            <ArrowLeft size={14} /> Back to Strike List
          </Link>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-slate-500">
            <Crosshair size={12} className="text-[#F43F5E]" />
            <span>Ohio Titan · {lead.state}</span>
          </div>
        </div>

        {/* Header block */}
        <section className="glass-strong rounded-sm p-6 sm:p-8 mb-8" data-testid="cockpit-header">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="w-16 h-16 rounded-sm border border-gold/40 flex items-center justify-center font-display text-gold text-2xl shrink-0">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="hud-label mb-2">Lead Cockpit</div>
              <h1 className="font-display text-3xl sm:text-4xl text-white font-light truncate">
                {lead.business_name}
              </h1>
              <div className="text-slate-400 text-sm mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>{lead.contact_name}</span>
                <span className="inline-flex items-center gap-1 text-slate-300">
                  <MapPin size={11} /> {lead.city}, OH
                </span>
                <span className="inline-flex items-center gap-1 text-slate-300">
                  <Wrench size={11} /> {lead.industry}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-start sm:items-end gap-2">
              <span
                className="inline-flex items-center px-3 py-1.5 rounded-sm text-[10px] font-display uppercase tracking-wider border"
                style={{
                  color: statusCfg.color,
                  backgroundColor: statusCfg.bg,
                  borderColor: statusCfg.border,
                }}
                data-testid="cockpit-status-pill"
              >
                {status}
              </span>
              {lead.updated_at && (
                <div className="text-[10px] text-slate-500 font-mono-aviation flex items-center gap-1">
                  <Clock size={10} /> Updated{" "}
                  {new Date(lead.updated_at).toLocaleString([], {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT: Editing panel (2 cols) */}
          <section className="lg:col-span-2 glass-strong rounded-sm p-6 sm:p-8" data-testid="cockpit-form">
            <div className="hud-label mb-6">Command Fields</div>

            {/* Status */}
            <div className="mb-6">
              <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">
                Status
              </label>
              <div className="flex flex-wrap gap-2" data-testid="cockpit-status-selector">
                {STATUS_OPTIONS.map((opt) => {
                  const cfg = STATUS_STYLE[opt];
                  const active = status === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setStatus(opt)}
                      data-testid={`cockpit-status-option-${opt.replace(/\s/g, "-").toLowerCase()}`}
                      className="px-4 py-2 rounded-sm text-xs font-display uppercase tracking-wider border transition-all"
                      style={
                        active
                          ? {
                              color: cfg.color,
                              backgroundColor: cfg.bg,
                              borderColor: cfg.border,
                              boxShadow: `0 0 18px ${cfg.bg}`,
                            }
                          : {
                              color: "#94A3B8",
                              backgroundColor: "transparent",
                              borderColor: "rgba(255,255,255,0.08)",
                            }
                      }
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Customer lifecycle block — conditional */}
            <div
              className="overflow-hidden transition-all duration-500 ease-out"
              style={{
                maxHeight: isCustomer ? "500px" : "0px",
                opacity: isCustomer ? 1 : 0,
                marginBottom: isCustomer ? "24px" : "0",
              }}
              data-testid="cockpit-customer-block"
              aria-hidden={!isCustomer}
            >
              <div className="rounded-sm border border-[#A78BFA]/30 bg-[#A78BFA]/[0.04] p-5">
                <div className="flex items-center gap-2 mb-5">
                  <Sparkles size={14} className="text-[#A78BFA]" />
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#A78BFA]">
                    Active Customer · Lifecycle
                  </div>
                </div>

                {/* Service Level */}
                <div className="mb-5">
                  <label
                    htmlFor="cockpit-service-level"
                    className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2"
                  >
                    Service Level
                  </label>
                  <select
                    id="cockpit-service-level"
                    value={serviceLevel}
                    onChange={(e) => setServiceLevel(e.target.value)}
                    data-testid="cockpit-service-level-select"
                    className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#A78BFA] appearance-none"
                    style={{
                      backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>\")",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 14px center",
                    }}
                  >
                    <option value="">— Select Service Level —</option>
                    {SERVICE_LEVELS.map((sl) => (
                      <option key={sl} value={sl}>
                        {sl}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Subscription Health */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">
                    Subscription Health
                  </label>
                  <div className="flex flex-wrap gap-2" data-testid="cockpit-health-selector">
                    {HEALTH_OPTIONS.map((h) => {
                      const active = subscriptionHealth === h.key;
                      return (
                        <button
                          key={h.key}
                          type="button"
                          onClick={() => setSubscriptionHealth(h.key)}
                          data-testid={`cockpit-health-option-${h.key.replace(/\s/g, "-").toLowerCase()}`}
                          className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-sm text-xs font-display border transition-all"
                          style={
                            active
                              ? {
                                  color: "#F8FAFC",
                                  backgroundColor: h.ring.replace("0.35", "0.14"),
                                  borderColor: h.ring,
                                  boxShadow: `0 0 16px ${h.ring}`,
                                }
                              : {
                                  color: "#94A3B8",
                                  backgroundColor: "transparent",
                                  borderColor: "rgba(255,255,255,0.08)",
                                }
                          }
                        >
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full"
                            style={{
                              backgroundColor: h.dot,
                              boxShadow: active ? `0 0 10px ${h.dot}` : "none",
                            }}
                          />
                          {h.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Weekly Leak */}
            <div className="mb-6">
              <label
                htmlFor="cockpit-weekly-leak"
                className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2"
              >
                Weekly Leak (USD) {isCustomer && <span className="text-[#A78BFA] normal-case tracking-normal ml-2">— now Recovered</span>}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">
                  <DollarSign size={16} />
                </span>
                <input
                  id="cockpit-weekly-leak"
                  type="number"
                  min="0"
                  step="1"
                  inputMode="decimal"
                  value={weeklyLeak}
                  onChange={(e) => setWeeklyLeak(e.target.value)}
                  placeholder="0"
                  data-testid="cockpit-weekly-leak-input"
                  className="w-full bg-[#060B14] border border-white/10 rounded-sm pl-11 pr-4 py-3 text-white text-lg font-mono-aviation focus:outline-none focus:border-gold"
                />
              </div>
              <div className="text-[10px] text-slate-500 mt-2">
                {isCustomer
                  ? "Once Active Customer, this rolls into the Grid Summary as Weekly Leak Recovered."
                  : "Estimated revenue leaking per week if the operational stack isn\u2019t activated."}
              </div>
            </div>

            {/* Sales Notes */}
            <div className="mb-6">
              <label
                htmlFor="cockpit-sales-notes"
                className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2"
              >
                Sales Notes
              </label>
              <textarea
                id="cockpit-sales-notes"
                rows={10}
                value={salesNotes}
                onChange={(e) => setSalesNotes(e.target.value)}
                placeholder="Objections, hot buttons, follow-up dates, decision-makers..."
                data-testid="cockpit-sales-notes-textarea"
                className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-gold resize-y leading-relaxed"
              />
              <div className="text-[10px] text-slate-500 mt-2">
                Free-form intelligence · persisted across sessions.
              </div>
            </div>

            {/* Save Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-4 border-t border-white/5">
              <div className="text-xs text-slate-500 flex-1">
                {isDirty ? (
                  <span className="text-[#FBBF24]">Unsaved changes</span>
                ) : (
                  <span className="text-slate-500">All changes saved</span>
                )}
              </div>
              <button
                onClick={save}
                disabled={saving || !isDirty}
                data-testid="cockpit-save-button"
                className="btn-gold px-6 py-3 rounded-sm text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Saving...
                  </>
                ) : (
                  <>
                    <Save size={14} /> Save Changes
                  </>
                )}
              </button>
            </div>
          </section>

          {/* RIGHT: Contact panel */}
          <aside className="glass-strong rounded-sm p-6 sm:p-8 h-fit" data-testid="cockpit-actions-panel">
            <div className="hud-label mb-6">Direct Actions</div>

            <ActionLink
              href={`tel:${lead.phone}`}
              icon={Phone}
              label="Direct Call"
              sub={lead.phone}
              color="#F5C842"
              testId="cockpit-action-call"
            />

            <a
              href="#outreach-composer"
              data-testid="cockpit-action-jump-composer"
              className="mt-3 flex items-center gap-3 p-4 rounded-sm border border-white/10 hover:border-gold/50 hover:bg-white/[0.04] transition-colors"
            >
              <div
                className="w-11 h-11 rounded-sm border flex items-center justify-center shrink-0"
                style={{ borderColor: "#60A5FA55", color: "#60A5FA" }}
              >
                <Mail size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-sm text-white">Outreach Composer</div>
                <div className="text-[10px] text-slate-500 truncate font-mono-aviation">
                  {lead.email}
                </div>
              </div>
            </a>

            {isCustomer && subscriptionHealth && (
              <div className="mt-8 pt-6 border-t border-white/5" data-testid="cockpit-health-summary">
                <div className="hud-label text-[9px] mb-2 flex items-center gap-1.5">
                  <Activity size={10} /> Current Health
                </div>
                {(() => {
                  const h = HEALTH_OPTIONS.find((x) => x.key === subscriptionHealth);
                  if (!h) return null;
                  return (
                    <div className="flex items-center gap-2.5 text-sm text-white">
                      <span
                        className="inline-block w-3 h-3 rounded-full"
                        style={{
                          backgroundColor: h.dot,
                          boxShadow: `0 0 12px ${h.dot}`,
                        }}
                      />
                      <span className="font-display">{h.label}</span>
                    </div>
                  );
                })()}
                {serviceLevel && (
                  <div className="text-[11px] text-slate-400 mt-2 font-mono-aviation">
                    Service · {serviceLevel}
                  </div>
                )}
              </div>
            )}
          </aside>
        </div>

        {/* Outreach Composer — full width */}
        <section
          id="outreach-composer"
          className="glass-strong rounded-sm p-6 sm:p-8 mt-6 scroll-mt-24"
          data-testid="outreach-composer"
        >
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <FileText size={14} className="text-gold" />
              <div className="hud-label">Outreach Composer</div>
            </div>
            <div className="text-[10px] text-slate-500 font-mono-aviation">
              Personalize before you slay · placeholders auto-filled
            </div>
          </div>

          {/* Recipient */}
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">To</div>
            <div
              className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-white/10 bg-[#060B14] text-sm text-white font-mono-aviation"
              data-testid="composer-recipient"
            >
              <Mail size={12} className="text-slate-400" />
              <span className="text-slate-300">{lead.contact_name}</span>
              <span className="text-slate-500">·</span>
              <span>{lead.email}</span>
            </div>
          </div>

          {/* Template selector */}
          <div className="mb-5">
            <label
              htmlFor="composer-template"
              className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2"
            >
              Email Template
            </label>
            <div className="relative">
              <select
                id="composer-template"
                value={selectedTemplateId}
                onChange={(e) => applyTemplate(e.target.value)}
                data-testid="composer-template-select"
                className="w-full bg-[#060B14] border border-white/10 rounded-sm pl-4 pr-10 py-3 text-white text-sm font-display focus:outline-none focus:border-gold appearance-none cursor-pointer"
              >
                {templates.length === 0 && <option>Loading templates...</option>}
                {templates.map((t) => (
                  <option key={t.id} value={t.id} data-testid={`composer-template-option-${t.id}`}>
                    {t.name}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
              />
            </div>
          </div>

          {/* Subject */}
          <div className="mb-5">
            <label
              htmlFor="composer-subject"
              className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2"
            >
              Subject
            </label>
            <input
              id="composer-subject"
              type="text"
              value={draftSubject}
              onChange={(e) => {
                setDraftSubject(e.target.value);
                if (emailState === "sent") setEmailState("idle");
              }}
              placeholder="Write a subject line..."
              data-testid="composer-subject-input"
              className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-gold"
            />
          </div>

          {/* Body */}
          <div className="mb-6">
            <label
              htmlFor="composer-body"
              className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2"
            >
              Body
            </label>
            <textarea
              id="composer-body"
              rows={12}
              value={draftBody}
              onChange={(e) => {
                setDraftBody(e.target.value);
                if (emailState === "sent") setEmailState("idle");
              }}
              placeholder="Compose your message..."
              data-testid="composer-body-textarea"
              className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-gold resize-y leading-relaxed font-mono-aviation"
              style={{ lineHeight: 1.7 }}
            />
            <div className="text-[10px] text-slate-500 mt-2">
              Edits stay in this draft until you send or switch templates.
            </div>
          </div>

          {/* Send Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-4 border-t border-white/5">
            <div className="text-xs text-slate-500 flex-1">
              {emailState === "sent" ? (
                <span className="text-[#34D399] inline-flex items-center gap-1.5">
                  <Check size={12} /> Sent to {lead.email}
                </span>
              ) : emailState === "sending" ? (
                <span className="text-[#60A5FA]">Dispatching...</span>
              ) : emailState === "error" ? (
                <span className="text-[#F43F5E]">Send failed — try again</span>
              ) : (
                <span>
                  Slay uses your <span className="text-white">edited</span> subject &amp; body — not the raw template.
                </span>
              )}
            </div>
            <button
              onClick={slaySend}
              disabled={emailState === "sending" || emailState === "sent" || !draftSubject.trim() || !draftBody.trim()}
              data-testid="composer-slay-button"
              className="btn-gold px-8 py-3 rounded-sm text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                boxShadow: emailState === "idle" ? "0 0 24px rgba(212,175,55,0.35)" : undefined,
              }}
            >
              {emailState === "sending" ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Sending...
                </>
              ) : emailState === "sent" ? (
                <>
                  <Check size={14} /> Sent
                </>
              ) : (
                <>
                  <Send size={14} /> Slay
                </>
              )}
            </button>
          </div>
        </section>

        {/* Amanda · Sales Critique */}
        <div className="mt-6">
          <SalesCritique lead={lead} />
        </div>
      </main>
    </div>
  );
}

function ActionLink({ href, icon: Icon, label, sub, color, testId, external }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      data-testid={testId}
      className="flex items-center gap-3 p-4 rounded-sm border border-white/10 hover:border-gold/50 hover:bg-white/[0.04] transition-colors"
    >
      <div
        className="w-11 h-11 rounded-sm border flex items-center justify-center shrink-0"
        style={{ borderColor: `${color}55`, color }}
      >
        <Icon size={16} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display text-sm text-white">{label}</div>
        <div className="text-[10px] text-slate-500 truncate font-mono-aviation">{sub}</div>
      </div>
    </a>
  );
}
